import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { format, addDays } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar,
  Check,
  X,
  RefreshCw,
  Clock,
  MessageSquare,
  User,
  Edit,
  Trash2,
  Globe,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { TimeOffRequest } from "./TimeOffRequestDialog";
import TimeOffBlockedPeriods from "./TimeOffBlockedPeriods";
import { formatTime12, combineDateAndTime, calcWorkedHours, generateRepeatDates } from "./utils";

type LeaveShiftType = {
  code: string;
  label: string;
  counts_as_worked_hours: boolean;
  is_active: boolean;
};

// A restricted user (see src/lib/permissions.ts, Settings -> Team Access)
// sees only time-off requests from their linked driver(s), and can only
// approve/reject/edit with edit rights on Roster.
const AdminTimeOffPanel: React.FC<{ scopedDriverNames?: string[] | null; canEdit?: boolean }> = ({
  scopedDriverNames = null,
  canEdit = true,
}) => {
  const [requests, setRequests] = useState<TimeOffRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [editingRequest, setEditingRequest] = useState<TimeOffRequest | null>(null);
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editReason, setEditReason] = useState("");
  const [leaveTypes, setLeaveTypes] = useState<LeaveShiftType[]>([]);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState<TimeOffRequest | null>(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState("LEAVE");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("roster_time_off_requests")
        .select("*")
        .order("requested_at", { ascending: false });

      if (filter === "pending") {
        query = query.eq("status", "pending");
      }
      if (scopedDriverNames) {
        query = query.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      setRequests((data || []) as TimeOffRequest[]);
    } catch (err: any) {
      console.error("Failed to fetch time off requests:", err);
      toast({ title: "Error", description: "Failed to load time off requests", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [filter, scopedDriverNames]);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("roster_shift_types")
        .select("code, label, counts_as_worked_hours, is_active")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;

      const filtered = (data || []).filter((st) => {
        const code = String(st.code || "").toUpperCase();
        return (
          !st.counts_as_worked_hours ||
          ["LEAVE", "SICK", "TRAINING", "TIME_OFF", "STANDBY"].includes(code)
        );
      }) as LeaveShiftType[];

      setLeaveTypes(filtered);

      if (filtered.some((t) => t.code === "LEAVE")) {
        setSelectedLeaveType("LEAVE");
      } else if (filtered.length > 0) {
        setSelectedLeaveType(filtered[0].code);
      }
    } catch (err) {
      console.error("Failed to load leave types:", err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchLeaveTypes();
  }, [fetchRequests, fetchLeaveTypes]);

  const createApprovedLeaveShifts = async (req: TimeOffRequest, shiftTypeCode: string, notes?: string) => {
    const adminNote = notes || adminNotes[req.id] || null;
    const rows: Record<string, any>[] = [];

    if (req.request_type === "partial_day") {
      const startTime = req.start_time || "09:00";
      const endTime = req.end_time || "17:00";
      rows.push({
        driver_name: req.driver_name,
        vehicle_number: null,
        shift_type_code: shiftTypeCode,
        status: "assigned",
        shift_date: req.start_date,
        start_at: combineDateAndTime(req.start_date, startTime),
        end_at: combineDateAndTime(req.start_date, endTime),
        start_hhmm: startTime,
        end_hhmm: endTime,
        break_minutes: 0,
        worked_hours: calcWorkedHours(startTime, endTime, 0),
        driver_visible_notes: req.reason || null,
        admin_notes: adminNote,
        published_at: new Date().toISOString(),
        published_by: "admin",
        approved_at: new Date().toISOString(),
        approved_by: "admin",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } else if (req.request_type === "full_day") {
      rows.push({
        driver_name: req.driver_name,
        vehicle_number: null,
        shift_type_code: shiftTypeCode,
        status: "assigned",
        shift_date: req.start_date,
        start_at: combineDateAndTime(req.start_date, "00:00"),
        end_at: combineDateAndTime(req.start_date, "23:59"),
        start_hhmm: "00:00",
        end_hhmm: "23:59",
        break_minutes: 0,
        worked_hours: 0,
        driver_visible_notes: req.reason || null,
        admin_notes: adminNote,
        published_at: new Date().toISOString(),
        published_by: "admin",
        approved_at: new Date().toISOString(),
        approved_by: "admin",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } else {
      const dates = generateRepeatDates("custom", req.start_date, req.end_date, [0, 1, 2, 3, 4, 5, 6]);
      for (const dateStr of dates) {
        rows.push({
          driver_name: req.driver_name,
          vehicle_number: null,
          shift_type_code: shiftTypeCode,
          status: "assigned",
          shift_date: dateStr,
          start_at: combineDateAndTime(dateStr, "00:00"),
          end_at: combineDateAndTime(dateStr, "23:59"),
          start_hhmm: "00:00",
          end_hhmm: "23:59",
          break_minutes: 0,
          worked_hours: 0,
          driver_visible_notes: req.reason || null,
          admin_notes: adminNote,
          published_at: new Date().toISOString(),
          published_by: "admin",
          approved_at: new Date().toISOString(),
          approved_by: "admin",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    }

    if (rows.length > 0) {
      const { error } = await supabase.from("roster_shifts").insert(rows);
      if (error) throw error;
    }
  };

  const handleApproveClick = (req: TimeOffRequest) => {
    if (!canEdit) return;
    setApprovingRequest(req);
    if (leaveTypes.some((t) => t.code === "LEAVE")) {
      setSelectedLeaveType("LEAVE");
    } else if (leaveTypes.length > 0) {
      setSelectedLeaveType(leaveTypes[0].code);
    }
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!canEdit || !approvingRequest) return;

    setProcessingId(approvingRequest.id);
    try {
      await createApprovedLeaveShifts(approvingRequest, selectedLeaveType);

      const { error } = await supabase
        .from("roster_time_off_requests")
        .update({
          status: "approved",
          admin_notes: adminNotes[approvingRequest.id] || null,
          resolved_at: new Date().toISOString(),
          resolved_by: "admin",
          updated_at: new Date().toISOString(),
        })
        .eq("id", approvingRequest.id);

      if (error) throw error;

      toast({
        title: "Approved",
        description: `Time off request approved and added to roster as ${selectedLeaveType}`,
      });

      setApproveDialogOpen(false);
      setApprovingRequest(null);
      fetchRequests();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to approve request", variant: "destructive" });
    } finally {
      setProcessingId(null);
    }
  };

  const handleResolve = async (requestId: string, newStatus: "denied") => {
    if (!canEdit) return;
    setProcessingId(requestId);
    try {
      const { error } = await supabase
        .from("roster_time_off_requests")
        .update({
          status: newStatus,
          admin_notes: adminNotes[requestId] || null,
          resolved_at: new Date().toISOString(),
          resolved_by: "admin",
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      if (error) throw error;

      toast({
        title: "Denied",
        description: "Time off request denied",
      });
      fetchRequests();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to process request", variant: "destructive" });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (requestId: string) => {
    if (!canEdit) return;
    if (!confirm("Delete this time off request?")) return;
    try {
      const { error } = await supabase.from("roster_time_off_requests").delete().eq("id", requestId);
      if (error) throw error;
      toast({ title: "Deleted", description: "Time off request deleted" });
      fetchRequests();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const openEdit = (req: TimeOffRequest) => {
    if (!canEdit) return;
    setEditingRequest(req);
    setEditStartDate(req.start_date);
    setEditEndDate(req.end_date);
    setEditStartTime(req.start_time || "");
    setEditEndTime(req.end_time || "");
    setEditReason(req.reason);
  };

  const handleSaveEdit = async () => {
    if (!canEdit || !editingRequest) return;
    setProcessingId(editingRequest.id);
    try {
      const { error } = await supabase
        .from("roster_time_off_requests")
        .update({
          start_date: editStartDate,
          end_date: editEndDate,
          start_time: editingRequest.request_type === "partial_day" ? editStartTime : null,
          end_time: editingRequest.request_type === "partial_day" ? editEndTime : null,
          reason: editReason,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingRequest.id);

      if (error) throw error;
      toast({ title: "Updated", description: "Time off request updated" });
      setEditingRequest(null);
      fetchRequests();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setProcessingId(null);
    }
  };

  const getTypeLabel = (req: TimeOffRequest) => {
    if (req.request_type === "partial_day") return "Partial Day";
    if (req.request_type === "full_day") return "Full Day";
    return "Multi-Day";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "border-amber-500 text-amber-400";
      case "approved": return "border-green-500 text-green-400";
      case "denied": return "border-red-500 text-red-400";
      default: return "border-gray-500 text-gray-400";
    }
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-4">
      <TimeOffBlockedPeriods canEdit={canEdit} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-400" />
            Time Off Requests
          </h3>
          {pendingCount > 0 && (
            <Badge className="bg-amber-600 text-white">{pendingCount} pending</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
            <button
              onClick={() => setFilter("pending")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === "pending" ? "bg-amber-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === "all" ? "bg-amber-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              All
            </button>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchRequests} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-orange-400" />
          <span className="ml-2 text-gray-400">Loading requests...</span>
        </div>
      ) : requests.length === 0 ? (
        <Card className="bg-gray-800/40 border-gray-700">
          <CardContent className="p-12 text-center">
            <Calendar className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No {filter === "pending" ? "pending " : ""}time off requests</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const isPending = req.status === "pending";
            const isProcessing = processingId === req.id;

            return (
              <Card
                key={req.id}
                className={`border transition-all ${
                  isPending
                    ? "border-amber-700/50 bg-amber-950/10"
                    : req.status === "approved"
                    ? "border-green-700/30 bg-green-950/10"
                    : "border-red-700/30 bg-red-950/10"
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center">
                            <User className="h-3.5 w-3.5 text-gray-300" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Employee</p>
                            <p className="text-sm font-medium text-white">{req.driver_name}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${getStatusColor(req.status)}`}>
                          {req.status}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-400">
                          {getTypeLabel(req)}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <Globe className="h-3.5 w-3.5 text-gray-500" />
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Dates</p>
                            <p className="text-sm text-gray-200">
                              {format(new Date(req.start_date + "T00:00:00"), "EEEE, d MMM yyyy")}
                              {req.start_date !== req.end_date && (
                                <> - {format(new Date(req.end_date + "T00:00:00"), "EEEE, d MMM yyyy")}</>
                              )}
                            </p>
                          </div>
                        </div>
                        {req.request_type === "partial_day" && req.start_time && req.end_time && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-gray-500" />
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider">Time</p>
                              <p className="text-sm text-gray-200">
                                {formatTime12(req.start_time)} - {formatTime12(req.end_time)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {req.reason && (
                        <div className="flex items-start gap-1.5">
                          <MessageSquare className="h-3.5 w-3.5 text-gray-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Comment</p>
                            <p className="text-sm text-gray-300">{req.reason}</p>
                          </div>
                        </div>
                      )}

                      <p className="text-[11px] text-gray-600">
                        Requested {format(new Date(req.requested_at), "d MMM yyyy 'at' h:mm a")}
                        {req.resolved_at && (
                          <> | {req.status === "approved" ? "Approved" : "Denied"} {format(new Date(req.resolved_at), "d MMM yyyy 'at' h:mm a")}</>
                        )}
                      </p>

                      {isPending && (
                        <Textarea
                          value={adminNotes[req.id] || ""}
                          onChange={(e) => setAdminNotes((prev) => ({ ...prev, [req.id]: e.target.value }))}
                          className="bg-gray-800 border-gray-600 text-white text-xs mt-1"
                          rows={1}
                          placeholder="Admin notes (optional)..."
                        />
                      )}

                      {!isPending && req.admin_notes && (
                        <div className="bg-gray-800/50 rounded px-2 py-1">
                          <p className="text-[11px] text-gray-500">Admin: {req.admin_notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      {!canEdit ? null : isPending ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApproveClick(req)}
                            disabled={isProcessing}
                            className="bg-green-600 hover:bg-green-700 text-white h-8 w-24"
                          >
                            {isProcessing ? (
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>
                                <Check className="h-3.5 w-3.5 mr-1" /> Approve
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResolve(req.id, "denied")}
                            disabled={isProcessing}
                            className="border-red-700 text-red-400 hover:bg-red-900/30 h-8 w-24"
                          >
                            <X className="h-3.5 w-3.5 mr-1" /> Deny
                          </Button>
                          <div className="flex gap-1 mt-1">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-500 hover:text-blue-400" onClick={() => openEdit(req)}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-500 hover:text-red-400" onClick={() => handleDelete(req.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-500 hover:text-red-400" onClick={() => handleDelete(req.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!editingRequest} onOpenChange={(v) => { if (!v) setEditingRequest(null); }}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-400" />
              Edit Time Off Request
            </DialogTitle>
          </DialogHeader>
          {editingRequest && (
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-3">
                <p className="text-sm text-gray-300">
                  <span className="text-gray-500">Driver:</span> {editingRequest.driver_name}
                </p>
                <p className="text-sm text-gray-300">
                  <span className="text-gray-500">Type:</span> {getTypeLabel(editingRequest)}
                </p>
              </div>
              <div className={editingRequest.request_type === "multi_day" ? "grid grid-cols-2 gap-3" : ""}>
                <div>
                  <Label className="text-gray-300">Start Date</Label>
                  <Input type="date" value={editStartDate} onChange={(e) => setEditStartDate(e.target.value)} className="bg-gray-800 border-gray-600" />
                </div>
                {editingRequest.request_type === "multi_day" && (
                  <div>
                    <Label className="text-gray-300">End Date</Label>
                    <Input type="date" value={editEndDate} onChange={(e) => setEditEndDate(e.target.value)} className="bg-gray-800 border-gray-600" />
                  </div>
                )}
              </div>
              {editingRequest.request_type === "partial_day" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-300">Start Time</Label>
                    <Input type="time" value={editStartTime} onChange={(e) => setEditStartTime(e.target.value)} className="bg-gray-800 border-gray-600" />
                  </div>
                  <div>
                    <Label className="text-gray-300">End Time</Label>
                    <Input type="time" value={editEndTime} onChange={(e) => setEditEndTime(e.target.value)} className="bg-gray-800 border-gray-600" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-gray-300">Reason</Label>
                <Textarea value={editReason} onChange={(e) => setEditReason(e.target.value)} className="bg-gray-800 border-gray-600" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRequest(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={processingId !== null}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-400" />
              Approve Time Off
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {approvingRequest && (
              <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                <p className="text-sm text-white font-medium">{approvingRequest.driver_name}</p>
                <p className="text-xs text-gray-400">
                  {approvingRequest.start_date === approvingRequest.end_date
                    ? format(new Date(approvingRequest.start_date + "T00:00:00"), "EEEE, d MMM yyyy")
                    : `${format(new Date(approvingRequest.start_date + "T00:00:00"), "d MMM yyyy")} - ${format(new Date(approvingRequest.end_date + "T00:00:00"), "d MMM yyyy")}`}
                </p>
                {approvingRequest.request_type === "partial_day" && approvingRequest.start_time && approvingRequest.end_time && (
                  <p className="text-xs text-gray-400">
                    {formatTime12(approvingRequest.start_time)} - {formatTime12(approvingRequest.end_time)}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label className="text-gray-300">Create approved roster entry as</Label>
              <select
                value={selectedLeaveType}
                onChange={(e) => setSelectedLeaveType(e.target.value)}
                className="w-full mt-1 bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white"
              >
                {leaveTypes.map((lt) => (
                  <option key={lt.code} value={lt.code}>
                    {lt.code} — {lt.label}
                  </option>
                ))}
              </select>
            </div>

            <p className="text-xs text-gray-400">
              This will approve the request and create visible leave/time-off shifts on the roster.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApproveConfirm} className="bg-green-600 hover:bg-green-700">
              Approve & Create Roster Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTimeOffPanel;